import type {Household, HouseholdAssignedTo, HouseholdMembership} from "../types";

export function getHouseholdOwnerMembership(
  memberships: HouseholdMembership[] | null | undefined,
): HouseholdMembership | null {
  if (!memberships?.length) return null;
  return memberships.find((row) => row.is_owner) ?? null;
}

export function getHouseholdOccupantMembership(
  memberships: HouseholdMembership[] | null | undefined,
): HouseholdMembership | null {
  if (!memberships?.length) return null;
  return memberships.find((row) => row.is_occupant) ?? null;
}

/** List/detail rows — owner, occupant, or combined owner-occupant. */
export type HouseholdMembershipListLine = {
  roleLabel: string;
  name: string;
  membership: HouseholdMembership;
  showOnboarding?: boolean;
};

export function getHouseholdMembershipListLines(
  memberships: HouseholdMembership[] | null | undefined,
): HouseholdMembershipListLine[] {
  const owner = getHouseholdOwnerMembership(memberships);
  const occupant = getHouseholdOccupantMembership(memberships);

  if (!owner && !occupant) return [];

  if (isOwnerOccupantSamePerson(memberships) && owner) {
    return [
      {
        roleLabel: "Owner & occupant",
        name: owner.name?.trim() || "—",
        membership: owner,
        showOnboarding: true,
      },
    ];
  }

  const lines: HouseholdMembershipListLine[] = [];
  if (owner) {
    lines.push({
      roleLabel: "Owner",
      name: owner.name?.trim() || "—",
      membership: owner,
    });
  }
  if (occupant) {
    lines.push({
      roleLabel: "Occupant",
      name: occupant.name?.trim() || "—",
      membership: occupant,
      showOnboarding: true,
    });
  }
  return lines;
}

/** Compact label for combobox/search — all member names when split. */
export function getHouseholdResidentDisplayName(household: Pick<Household, "memberships">): string {
  const lines = getHouseholdMembershipListLines(household.memberships);
  if (!lines.length) return "—";
  if (lines.length === 1) return lines[0].name;
  return lines.map((line) => line.name).join(" · ");
}

/** Onboarding badge source — occupant row carries onboarding fields on list/show. */
export function getHouseholdResidentOnboardingMembership(
  memberships: HouseholdMembership[] | null | undefined,
): HouseholdMembership | null {
  return getHouseholdOccupantMembership(memberships) ?? getHouseholdOwnerMembership(memberships);
}

export function membershipToAssignedTo(
  membership: HouseholdMembership | null | undefined,
): HouseholdAssignedTo | null {
  if (!membership?.name?.trim()) return null;
  return {
    id: membership.user_id,
    name: membership.name,
    user_name: membership.user_name,
    email: membership.email,
    email_verified_at: membership.email_verified_at,
    is_placeholder: membership.is_placeholder,
    welcome_sent_at: membership.welcome_sent_at,
    has_deliverable_email: membership.has_deliverable_email,
    onboarding_status: membership.onboarding_status,
    onboarding_status_label: membership.onboarding_status_label,
    requires_profile_completion: membership.requires_profile_completion,
    invitation: membership.invitation,
  };
}

export function isOwnerOccupantSamePerson(
  memberships: HouseholdMembership[] | null | undefined,
): boolean {
  const owner = getHouseholdOwnerMembership(memberships);
  const occupant = getHouseholdOccupantMembership(memberships);
  if (!owner || !occupant) return false;
  return owner.user_id === occupant.user_id;
}

/** Owner still occupies the unit — invite tenant is available. */
export function canInviteHouseholdOccupantFromMemberships(
  memberships: HouseholdMembership[] | null | undefined,
): boolean {
  const owner = getHouseholdOwnerMembership(memberships);
  if (!owner) return false;
  return isOwnerOccupantSamePerson(memberships);
}

/** Separate non-placeholder tenant — revoke is available. */
export function canRevokeHouseholdOccupantFromMemberships(
  memberships: HouseholdMembership[] | null | undefined,
): boolean {
  const owner = getHouseholdOwnerMembership(memberships);
  const occupant = getHouseholdOccupantMembership(memberships);
  if (!owner || !occupant) return false;
  if (owner.user_id === occupant.user_id) return false;
  return occupant.is_placeholder !== true;
}
